import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "scenta-hq-install-dismissed-at";
const DISMISS_DAYS = 14;

export function PwaInstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosTip, setShowIosTip] = useState(false);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DAYS * 86400 * 1000) return;

    // Already installed?
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    // Suppress on kiosk path so it never appears on the kiosk hardware
    if (window.location.pathname.startsWith("/kiosk")) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // iOS Safari doesn't fire beforeinstallprompt — show a hint instead.
    const ua = navigator.userAgent;
    const isIos = /iPad|iPhone|iPod/.test(ua) && !("MSStream" in window);
    const inSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIos && inSafari) {
      const t = setTimeout(() => setShowIosTip(true), 8000);
      return () => { window.removeEventListener("beforeinstallprompt", handler); clearTimeout(t); };
    }
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setEvt(null);
    setShowIosTip(false);
  }

  async function install() {
    if (!evt) return;
    await evt.prompt();
    await evt.userChoice;
    setEvt(null);
  }

  if (!evt && !showIosTip) return null;

  return (
    <div className="fixed bottom-20 right-4 z-30 max-w-sm max-w-[calc(100vw-2rem)] md:bottom-24 md:right-6">
      <div className="card flex items-start gap-3 shadow-soft ring-1 ring-teal-mint">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-mint text-teal-dark">
          <Download size={20} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Install Sunday State HQ</div>
          {evt ? (
            <p className="mt-1 text-xs text-gray2">Add HQ to your home screen for one-tap access.</p>
          ) : (
            <p className="mt-1 text-xs text-gray2">
              Tap the <strong>Share</strong> button at the bottom of Safari, then <strong>Add to Home Screen</strong>.
            </p>
          )}
          {evt && (
            <div className="mt-3 flex gap-2">
              <button onClick={install} className="btn text-xs">Install</button>
              <button onClick={dismiss} className="btn-secondary text-xs">Not now</button>
            </div>
          )}
        </div>
        <button onClick={dismiss} className="text-gray2 hover:text-charcoal" aria-label="Dismiss">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
